'use client'

import { MemoryTask } from '@/api/services/types/questionTypes'
import { Card, CardContent } from '@/components/card'
import { Input } from '@/components/forms/inputs/text/Input'
import { Button } from '@/components/ui/common/Button'
import { useTestResponse } from '@/hooks/useTestResponse'
import { cn } from '@/utils/common'
import * as React from 'react'
import StartCard from '../components/StartCard'

interface WorkingMemoryTaskProps {
  data?: MemoryTask
  moduleId: string
  questionId: string
  className?: string
  onComplete?: () => void
  onStart?: () => void
}

type TaskState = 'intro' | 'memorize' | 'response' | 'feedback' | 'complete'

export function WorkingMemoryTask({
  data,
  moduleId,
  questionId,
  className,
  onComplete,
  onStart
}: WorkingMemoryTaskProps) {
  const { addQuestionResponse } = useTestResponse()
  const [state, setState] = React.useState<TaskState>('intro')
  const [userAnswer, setUserAnswer] = React.useState('')
  const [startTime, setStartTime] = React.useState<number | null>(null)
  const [responseTime, setResponseTime] = React.useState<number>(0)
  const [currentDigitIndex, setCurrentDigitIndex] = React.useState(0)
  const [remainingTime, setRemainingTime] = React.useState<number | null>(null)

  // Używamy danych z API lub domyślnych wartości
  const digitCount = data?.digitCount || 5
  const memorizationTimeSeconds = data?.memorizationTimeSeconds || 5
  const answerTimeSeconds = data?.answerTimeSeconds || 10
  const responseMode = data?.responseMode || 'MANUAL'
  const generatedNumber = data?.generatedNumber || generateRandomNumber(digitCount)
  const choiceOptions = data?.choiceOptions || []

  function generateRandomNumber(length: number): string {
    return Array.from({ length }, () => Math.floor(Math.random() * 10)).join('')
  }

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  React.useEffect(() => {
    startTask()
  }, [])

  const startTask = () => {
    onStart?.()
    setState('memorize')
    setStartTime(Date.now())
    setCurrentDigitIndex(0)
  }

  const showNextDigit = () => {
    if (currentDigitIndex < digitCount - 1) {
      setCurrentDigitIndex((prev) => prev + 1)
    } else {
      handleMemorizationComplete()
    }
  }

  const handleMemorizationComplete = () => {
    setState('response')
  }

  const handleSubmit = async () => {
    if (!startTime) return

    const endTime = Date.now()
    const timeSpent = endTime - startTime

    setResponseTime(timeSpent)
    setState('feedback')

    // Zapisz odpowiedź do TestResponseProvider
    await addQuestionResponse(moduleId, {
      questionId,
      questionType: 'MEMORY_TASK',
      memoryTaskAnswer: {
        userAnswer,
        responseTimeMillis: timeSpent
      }
    })

    setTimeout(() => {
      setState('complete')
      onComplete?.()
    }, 1000)
  }

  const handleChoiceSelect = (choice: string) => {
    setUserAnswer(choice)
    handleSubmit()
  }

  React.useEffect(() => {
    if (state === 'memorize') {
      const timer = setTimeout(
        () => {
          showNextDigit()
        },
        (memorizationTimeSeconds * 1000) / digitCount
      )

      return () => clearTimeout(timer)
    }
  }, [state, currentDigitIndex, memorizationTimeSeconds, digitCount])

  React.useEffect(() => {
    if (state === 'response') {
      setRemainingTime(answerTimeSeconds)
      const timer = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev === null || prev <= 0) {
            clearInterval(timer)
            handleSubmit()
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [state, answerTimeSeconds])

  if (state === 'intro') {
    return (
      <StartCard
        description={`Zapamiętaj ${digitCount} cyfr, które będą pokazane po kolei w odwrotnej kolejności. Każda cyfra będzie widoczna przez ${(memorizationTimeSeconds / digitCount).toFixed(1)} sekundy. Następnie wpisz wszystkie cyfry w polu odpowiedzi.`}
        onStart={startTask}
        subDescription={`Będziesz mieć ${answerTimeSeconds} sekund na wpisanie odpowiedzi.`}
        title="Zadanie z pamięci operacyjnej"
      />
    )
  }

  return (
    <Card className={cn('relative mx-auto w-full max-w-md', className)}>
      <CardContent className="pt-6 pb-6">
        {remainingTime !== null && state === 'response' && (
          <div className="absolute -top-8 right-0 text-sm font-medium text-gray-600 bg-transparent px-3 py-1 rounded-md">
            Pozostały czas: {formatTime(remainingTime)}
          </div>
        )}
        {state === 'memorize' && (
          <div className="flex flex-col items-center gap-6">
            <h2 className="text-center text-xl font-bold">Zapamiętaj cyfrę</h2>
            <div className="flex h-32 w-32 items-center justify-center rounded-md bg-[#2bcaca]/10">
              <span className="text-6xl font-bold">
                {generatedNumber[digitCount - 1 - currentDigitIndex]}
              </span>
            </div>
            <p className="text-center text-sm text-gray-500">
              Cyfra {currentDigitIndex + 1} z {digitCount}
            </p>
          </div>
        )}

        {state === 'response' && (
          <div className="flex flex-col items-center gap-6">
            <h2 className="text-center text-xl font-bold">
              Wpisz wszystkie zapamiętane cyfry
            </h2>
            {responseMode === 'MANUAL' ? (
              <>
                <Input
                  autoFocus
                  className="text-center text-lg"
                  inputMode="numeric"
                  maxLength={digitCount}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setUserAnswer(e.target.value)
                  }
                  pattern="[0-9]*"
                  type="text"
                  value={userAnswer}
                />
                <Button
                  className="bg-[#2bcaca] hover:bg-[#2b9999]"
                  disabled={!userAnswer}
                  onClick={handleSubmit}
                >
                  Sprawdź
                </Button>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {choiceOptions.map((choice) => (
                  <Button
                    className="text-xl"
                    key={choice}
                    onClick={() => handleChoiceSelect(choice)}
                  >
                    {choice}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        {state === 'feedback' && (
          <div className="flex flex-col items-center gap-6">
            <h2 className="text-center text-xl font-bold">Sprawdzanie odpowiedzi...</h2>
          </div>
        )}

        {state === 'complete' && (
          <div className="flex flex-col items-center gap-6">
            <h2 className="text-center text-xl font-bold">Zadanie zakończone</h2>
            <p>Twój czas odpowiedzi: {(responseTime / 1000).toFixed(2)} sekund</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
